<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import InputGroupAddon from "primevue/inputgroupaddon";
import InputGroup from "primevue/inputgroup";
import backendCall from "../../services/AxiosService";
import {useToast} from "primevue/usetoast";
import { getAllLocations, searchItemsByLocation, getItemLocationsByLocationId, getItemQuantityAtLocation, InventoryItem, InventoryItemLocationWithDetails } from "../../services/inventory";
import AutoComplete from 'primevue/autocomplete';
import Dropdown from 'primevue/dropdown';
import InputNumber from 'primevue/inputnumber';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

const toast = useToast();
const props = defineProps({clipNumber: String, eventId: Number, listType: String});
const visible = defineModel<boolean>();
const emit = defineEmits(['update-clips']);

const fuelOptions = ref(['E', '1/8', '1/4', '3/8', '5/16', '7/16', '1/2', '9/16', '5/8', '11/16', '3/4', '13/16', '7/8', 'F']);

// Inventory items
const inventoryLocations = ref<{ id: number | null, name: string, itemCount?: number, selectedItemQuantity: number }[]>([]);
const selectedLocation = ref<number | null>(null);
const inventoryItemSuggestions = ref<InventoryItem[]>([]);
const selectedInventoryItem = ref<InventoryItem | null>(null);
const selectedQuantity = ref<number>(1);
const selectedAddLocation = ref<number | null>(null);
const isSearchingItems = ref<boolean>(false);
const availableQuantityAtLocation = ref<number>(0);
let searchTimeout: NodeJS.Timeout;

interface InventoryItemUsed {
  item_id: number;
  location_id: number;
  quantity: number;
  item_name: string;
  location_name: string;
  unit_of_measure: string;
}

const pretripFormData = ref({
  eventId: props.eventId,
  clipNumber: props.clipNumber,
  fuelLevel: null,
  notes: null,
  pretripPass: null,
  engineHours: null,
  inventoryItems: [] as InventoryItemUsed[],
});

// Load inventory data
const loadInventoryData = async () => {
  try {
    // Load all inventory locations
    const locationsResponse = await getAllLocations();

    // Ensure we have an array before mapping (defensive coding for production)
    const locations = Array.isArray(locationsResponse) ? locationsResponse : [];

    // Fetch item counts for each location
    const locationPromises = locations.map(async (loc) => {
      try {
        const itemsAtLocation = await getItemLocationsByLocationId(loc.id, false);
        return {
          id: loc.id,
          name: loc.name,
          itemCount: itemsAtLocation.length,
          selectedItemQuantity: 0 // Add field for selected item quantity
        };
      } catch (error) {
        console.error(`Error fetching items for location ${loc.id}:`, error);
        return {
          id: loc.id,
          name: loc.name,
          itemCount: 0,
          selectedItemQuantity: 0
        };
      }
    });

    // Update locations with item counts
    const locationsWithCounts = await Promise.all(locationPromises);
    
    // Sort locations alphabetically by name
    locationsWithCounts.sort((a, b) => a.name.localeCompare(b.name));
    
    // Add "All Locations" option at the beginning
    inventoryLocations.value = [
      { id: null, name: 'All Locations', itemCount: undefined, selectedItemQuantity: 0 },
      ...locationsWithCounts
    ];
  } catch (error) {
    console.error('Error loading inventory data:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load inventory data',
      life: 3000
    });
  }
};

// Search inventory items based on input in real-time with debouncing
const searchInventoryItems = (event: { query: string }) => {
  // Clear previous timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (!event.query || event.query.trim().length < 2) {
    inventoryItemSuggestions.value = [];
    return;
  }

  isSearchingItems.value = true;

  // Debounce the search by 300ms to avoid too many API calls
  searchTimeout = setTimeout(async () => {
    try {
      // Use real-time search with location filtering if a location is selected
      const items = await searchItemsByLocation(
        event.query.trim(), 
        selectedLocation.value, 
        true
      );
      
      inventoryItemSuggestions.value = items as InventoryItem[];
    } catch (error) {
      console.error('Error searching inventory items:', error);
      inventoryItemSuggestions.value = [];
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to search inventory items',
        life: 3000
      });
    } finally {
      isSearchingItems.value = false;
    }
  }, 300);
};

// Update available quantity at the current location
const updateAvailableQuantity = async () => {
  if (!selectedInventoryItem.value) {
    availableQuantityAtLocation.value = 0;
    return;
  }

  const locationToCheck = selectedLocation.value === null ? selectedAddLocation.value : selectedLocation.value;
  
  if (!locationToCheck) {
    availableQuantityAtLocation.value = 0;
    return;
  }

  try {
    const quantity = await getItemQuantityAtLocation(selectedInventoryItem.value.id, locationToCheck);
    availableQuantityAtLocation.value = quantity;
  } catch (error) {
    console.error('Error getting available quantity:', error);
    availableQuantityAtLocation.value = 0;
  }
};

// Handle location change
const onLocationChange = () => {
  // Clear selected item and suggestions when location changes
  selectedInventoryItem.value = null;
  inventoryItemSuggestions.value = [];
  selectedAddLocation.value = null;
  availableQuantityAtLocation.value = 0;
};

// Add inventory item to the list
const addInventoryItem = async () => {
  // Determine which location to use
  let locationToUse: number | null = null;
  
  if (selectedLocation.value === null) {
    // "All Locations" is selected, use the specific add location
    if (!selectedAddLocation.value) {
      toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a specific location to add the item to.',
        life: 3000
      });
      return;
    }
    locationToUse = selectedAddLocation.value;
  } else {
    // Specific location is selected
    locationToUse = selectedLocation.value;
  }

  if (!selectedInventoryItem.value || !selectedQuantity.value || selectedQuantity.value <= 0) {
    toast.add({
      severity: 'warn',
      summary: 'Warning',
      detail: 'Please select an item and enter a valid quantity.',
      life: 3000
    });
    return;
  }

  // Validate that the selected item is a valid inventory item (has an id property)
  if (!selectedInventoryItem.value.id) {
    toast.add({
      severity: 'error',
      summary: 'Invalid Item',
      detail: 'Please select a valid item from the suggestions list.',
      life: 3000
    });
    return;
  }

  // Validate available quantity at the selected location
  const availableQty = await getItemQuantityAtLocation(selectedInventoryItem.value.id, locationToUse);
  
  if (availableQty <= 0) {
    toast.add({
      severity: 'error',
      summary: 'No Inventory Available',
      detail: `There are no ${selectedInventoryItem.value.name} available at the selected location.`,
      life: 5000
    });
    return;
  }

  if (selectedQuantity.value > availableQty) {
    toast.add({
      severity: 'error',
      summary: 'Insufficient Inventory',
      detail: `Only ${availableQty} ${selectedInventoryItem.value.unit_of_measure} available. Requested: ${selectedQuantity.value}`,
      life: 5000
    });
    return;
  }

  // Find the location name
  const locationName = inventoryLocations.value.find(l => l.id === locationToUse)?.name || 'Unknown Location';

  // Add the item to the list
  pretripFormData.value.inventoryItems.push({
    item_id: selectedInventoryItem.value.id,
    location_id: locationToUse,
    quantity: selectedQuantity.value,
    item_name: selectedInventoryItem.value.name,
    location_name: locationName,
    unit_of_measure: selectedInventoryItem.value.unit_of_measure
  });

  // Reset selection
  selectedInventoryItem.value = null;
  selectedQuantity.value = 1;
  selectedAddLocation.value = null;
};

// Remove inventory item from the list
const removeInventoryItem = (index: number) => {
  pretripFormData.value.inventoryItems.splice(index, 1);
};

const isSubmitting = ref(false);

const submitClipPretrip = async () => {
  try {
    isSubmitting.value = true;
    let url;
    if (props.listType === 'pretrip-required') {
      url = '/clip-ons/pretrip'
    } else if (props.listType === 'failed-pretrip') {
      url = '/clip-ons/pretrip/resolve'
    } else { throw new Error('Invalid List Type')
    }

    if (pretripFormData.value.pretripPass === null) {
      toast.add({severity: 'error', summary: 'Error!', detail: 'Please select either pass or fail.', life: 5000});
      return;
    }

    if (!pretripFormData.value.engineHours) {
      toast.add({severity: 'error', summary: 'Error!', detail: 'Please enter an engine hours value.', life: 5000});
      return;
    }

    if (!pretripFormData.value.fuelLevel) {
      toast.add({severity: 'error', summary: 'Error!', detail: 'Please enter a fuel level value.', life: 5000});
      return;
    }

    // Prepare the data to submit
    const dataToSubmit = {
      ...pretripFormData.value,
      // Only include inventory items if there are any
      inventoryItems: pretripFormData.value.inventoryItems.length > 0 ? pretripFormData.value.inventoryItems : undefined
    };

    // Log the data being submitted
    console.log('Submitting pretrip data:', dataToSubmit);

    // Show a toast with inventory items count
    if (pretripFormData.value.inventoryItems.length > 0) {
      toast.add({
        severity: 'info',
        summary: 'Inventory Items',
        detail: `Submitting ${pretripFormData.value.inventoryItems.length} inventory items with this pretrip.`,
        life: 5000
      });
    }

    const postClipPretripCall = await backendCall.post(url, dataToSubmit);
    console.log('Pretrip submission response:', postClipPretripCall);
    if (!postClipPretripCall) {
      throw new Error('Could not submit clip-on pretrip')
    }
    emit('update-clips');
    visible.value = false;
    toast.add({
      severity: 'success',
      summary: 'Success!',
      detail: `${pretripFormData.value.clipNumber} pretrip has been recorded.`,
      life: 3000
    });
  } catch (e) {
    console.error('Error submitting pretrip:', e);
    toast.add({severity: 'error', summary: 'Error!', detail: 'Could not submit clip-on pretrip.', life: 5000});
  } finally {
    isSubmitting.value = false;
  }
}

// Watch for changes to update available quantity
watch([selectedInventoryItem, selectedAddLocation], () => {
  updateAvailableQuantity();
  updateSelectedItemQuantities();
});

// Load inventory data when component is mounted
onMounted(() => {
  loadInventoryData();
});

// Cleanup timeout when component is unmounted
onUnmounted(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
});

// Update quantities of selected item at each location
const updateSelectedItemQuantities = async () => {
  if (!selectedInventoryItem.value) {
    // Reset quantities if no item selected
    inventoryLocations.value.forEach(location => {
      if (location.id !== null) {
        location.selectedItemQuantity = 0;
      }
    });
    return;
  }

  // Update quantities for the selected item at each location
  const updatePromises = inventoryLocations.value
    .filter(loc => loc.id !== null)
    .map(async (location) => {
      try {
        const quantity = await getItemQuantityAtLocation(selectedInventoryItem.value!.id, location.id!);
        location.selectedItemQuantity = quantity;
      } catch (error) {
        console.error(`Error getting quantity for item ${selectedInventoryItem.value!.id} at location ${location.id}:`, error);
        location.selectedItemQuantity = 0;
      }
    });

  await Promise.all(updatePromises);

  // Sort locations: "All Locations" first, then by selectedItemQuantity (highest first), then alphabetically
  inventoryLocations.value.sort((a, b) => {
    // "All Locations" (id: null) always comes first
    if (a.id === null) return -1;
    if (b.id === null) return 1;
    
    // Sort by selectedItemQuantity (highest first)
    if (a.selectedItemQuantity !== b.selectedItemQuantity) {
      return b.selectedItemQuantity - a.selectedItemQuantity;
    }
    
    // If quantities are equal, sort alphabetically by name
    return a.name.localeCompare(b.name);
  });
};
</script>

<template>

  <Dialog v-model:visible="visible" modal :style="{ width: '25rem' }">
    <template #header>
      <h1 class="m-0 text-surface-900 dark:text-surface-0 font-semibold text-xl leading-normal">Pretrip {{ clipNumber }}</h1>
    </template>
    <template #default class="pb-0">
    <form class="flex flex-col gap-4 mt-4">
      <div class="flex gap-4">
        <div class="w-full">
          <label for="fuel-level" class="block mb-1 text-color text-base">Fuel Level</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg"></i>
            </InputGroupAddon>
            <Dropdown v-model="pretripFormData.fuelLevel" :options="fuelOptions.toReversed()" id="fuel-level" inputId="fuel-level"/>
          </InputGroup>
        </div>
        <div class="w-full">
          <label for="engine-hours" class="block mb-1 text-color text-base">Engine Hours</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-clock text-lg"></i>
            </InputGroupAddon>
            <input-number id="engine-hours" v-model.number="pretripFormData.engineHours" :min-fraction-digits="1" :max-fraction-digits="1" :use-grouping="false"/>
          </InputGroup>
        </div>
      </div>
      <!-- Inventory Items Section -->
      <div class="flex flex-col justify-center items-center gap-2 mt-4">
        <div class="w-full">
          <label class="block mb-1 text-color text-base font-semibold">Parts Used</label>

          <!-- Location and Item Selection -->
          <div class="flex flex-col gap-2 mb-2">
            <div class="w-full">
              <label for="location" class="block mb-1 text-color text-sm">Location</label>
              <Dropdown id="location" v-model="selectedLocation" :options="inventoryLocations" 
                        optionValue="id" placeholder="Select Location" 
                        class="w-full" @change="onLocationChange">
                <template #value="slotProps">
                  <div class="flex align-items-center">
                    <span v-if="slotProps.value !== undefined">{{ inventoryLocations.find(loc => loc.id === slotProps.value)?.name }}</span>
                    <span v-else>Select Location</span>
                  </div>
                </template>
                <template #option="slotProps">
                  <div class="flex justify-content-between align-items-center">
                    <span>{{ slotProps.option.name }}</span>
                    <span v-if="slotProps.option.itemCount !== undefined" class="text-sm text-gray-500">
                      ({{ slotProps.option.itemCount }} items)
                    </span>
                    <span v-else class="text-sm text-blue-500">
                      (Search all locations)
                    </span>
                  </div>
                </template>
              </Dropdown>
            </div>

            <div class="w-full">
              <label for="inventory-item" class="block mb-1 text-color text-sm">Item</label>
              <AutoComplete id="inventory-item" v-model="selectedInventoryItem" :suggestions="inventoryItemSuggestions"
                            optionLabel="name" class="w-full" @complete="searchInventoryItems"
                            :loading="isSearchingItems" placeholder="Type to search items..." :minLength="2">
                <template #option="slotProps">
                  <div class="flex flex-col">
                    <span>{{ slotProps.option.name }}</span>
                    <small v-if="slotProps.option.sku" class="text-gray-500">SKU: {{ slotProps.option.sku }}</small>
                    <small v-if="slotProps.option.part_number" class="text-gray-500">Part #: {{ slotProps.option.part_number }}</small>
                    <small class="text-gray-500">Unit: {{ slotProps.option.unit_of_measure }}</small>
                    <small v-if="slotProps.option.current_quantity !== undefined" class="text-blue-600 font-medium">
                      Available: {{ slotProps.option.current_quantity }} {{ slotProps.option.unit_of_measure }}
                    </small>
                    <small v-if="selectedLocation === null && slotProps.option.locations && slotProps.option.locations.length > 0" class="text-green-600">
                      Locations: {{ slotProps.option.locations.map((l: any) => l.location?.name).filter(Boolean).join(', ') }}
                    </small>
                  </div>
                </template>
              </AutoComplete>
              <small class="text-gray-500 mt-1">
                Type at least 2 characters to search for items with real-time inventory
                <span v-if="selectedLocation === null"> across all locations</span>
              </small>
            </div>

            <!-- Specific Location for Adding Item (when All Locations is selected for search) -->
            <div v-if="selectedLocation === null && selectedInventoryItem" class="w-full">
              <label for="add-location" class="block mb-1 text-color text-sm">Location for Item *</label>
              <Dropdown id="add-location" v-model="selectedAddLocation" :options="inventoryLocations.filter(loc => loc.id !== null)" 
                        optionValue="id" optionLabel="name" placeholder="Select location to add item" 
                        class="w-full">
                <template #option="slotProps">
                  <div class="flex justify-content-between align-items-center">
                    <span>{{ slotProps.option.name }}</span>
                    <span v-if="selectedInventoryItem" class="text-sm" 
                          :class="slotProps.option.selectedItemQuantity > 0 ? 'text-green-600 font-medium' : 'text-red-500'">
                      {{ slotProps.option.selectedItemQuantity }} {{ selectedInventoryItem.unit_of_measure }} available
                    </span>
                    <span v-else class="text-sm text-gray-500">
                      ({{ slotProps.option.itemCount }} items)
                    </span>
                  </div>
                </template>
              </Dropdown>
              <small class="text-gray-500 mt-1">You must select a specific location to add the item to your pretrip</small>
            </div>

            <div class="w-full">
              <label for="quantity" class="block mb-1 text-color text-sm">Quantity</label>
              <div class="flex">
                <InputNumber id="quantity" v-model="selectedQuantity" :min="1" :max="availableQuantityAtLocation || undefined" class="w-full" />
                <span v-if="selectedInventoryItem" class="flex items-center px-2 text-gray-500">{{ selectedInventoryItem.unit_of_measure }}</span>
                <Button icon="pi pi-plus" class="ml-2" @click="addInventoryItem" 
                        :disabled="!selectedInventoryItem || availableQuantityAtLocation <= 0 || !selectedQuantity || selectedQuantity <= 0" />
              </div>
              <!-- Show available quantity at selected location -->
              <div v-if="selectedInventoryItem && (selectedLocation !== null || selectedAddLocation)" class="mt-1">
                <small v-if="availableQuantityAtLocation > 0" class="text-green-600 font-medium">
                  Available: {{ availableQuantityAtLocation }} {{ selectedInventoryItem.unit_of_measure }} at selected location
                </small>
                <small v-else class="text-red-600 font-medium">
                  No inventory available at selected location
                </small>
              </div>
            </div>
          </div>

          <!-- Selected Items Table -->
          <div v-if="pretripFormData.inventoryItems.length > 0" class="mt-2 border rounded-md overflow-x-auto">
            <DataTable :value="pretripFormData.inventoryItems" class="p-datatable-sm" responsiveLayout="scroll">
              <Column field="item_name" header="Item"></Column>
              <Column field="location_name" header="Location"></Column>
              <Column field="quantity" header="Quantity">
                <template #body="slotProps">
                  {{ slotProps.data.quantity }} {{ slotProps.data.unit_of_measure }}
                </template>
              </Column>
              <Column header="Actions" style="width: 5rem">
                <template #body="slotProps">
                  <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" 
                          @click="removeInventoryItem(slotProps.index)" />
                </template>
              </Column>
            </DataTable>
          </div>
          <div v-else class="text-gray-500 text-sm mt-2 italic">
            No parts used for this pretrip
          </div>
        </div>

        <div class="w-full mt-4">
          <label for="notes" class="block mb-1 text-color text-base">Notes</label>
          <Textarea id="notes" name="notes" v-model="pretripFormData.notes" rows="3" class="w-full"></Textarea>
        </div>
        <div class="mt-2">
          <SelectButton v-model="pretripFormData.pretripPass" :options="[{name: 'Pass', value: true},{name: 'Fail', value: false}]" option-label="name" option-value="value" aria-labelledby="basic">
          </SelectButton>
        </div>
      </div>
    </form>
      </template>
    <template #footer>
      <div class="flex gap-4 justify-end border-t border-surface pt-8">
        <Button label="Cancel" @click="visible = false" class="p-button-text"></Button>
        <Button label="Submit" @click="submitClipPretrip" :disabled="!pretripFormData.clipNumber || !pretripFormData.engineHours || !pretripFormData.fuelLevel || pretripFormData.pretripPass == null || isSubmitting" class="p-button-rounded"></Button>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>

</style>
